import { useState } from "react";
import { motion } from "framer-motion";
import { Reveal, ClipImage, SectionHead } from "./Reveal";
import { EASE } from "../lib/site";

const SHAPES = {
  Mandorla: "M50 8 C70 8 86 42 86 84 L86 152 L14 152 L14 84 C14 42 30 8 50 8 Z",
  Quadrata: "M50 16 C84 16 86 16 86 18 L86 152 L14 152 L14 18 C14 16 16 16 50 16 Z",
  Squadrata: "M50 14 C78 14 86 16 86 36 L86 152 L14 152 L14 36 C14 16 22 14 50 14 Z",
  Ovale: "M50 12 C76 12 86 34 86 64 L86 152 L14 152 L14 64 C14 34 24 12 50 12 Z",
};

const TECH = [
  ["Gel", "Flessibile e brillante, dona un effetto naturale e leggero. Perfetto per allungare e uniformare."],
  ["Acrygel", "Unisce la resistenza dell'acrilico alla lavorabilità del gel: leggero e molto robusto."],
  ["Allungamento", "Su cartina o dual form, per ottenere lunghezza e forma desiderate partendo dall'unghia naturale."],
  ["Refill", "Il mantenimento periodico, indicativamente ogni 3–4 settimane, per riempire la ricrescita."],
  ["Semipermanente", "Prodotto flessibile con una leggera bombatura che viene applicato su un'unghia corta."],
];

const ShapePicker = () => {
  const [shape, setShape] = useState("Mandorla");
  return (
    <Reveal className="flex flex-col justify-between rounded-[2rem] bg-[var(--ink)] p-8 text-[var(--cream)] md:p-10" data-testid="nails-shape-picker">
      <p className="text-xs uppercase tracking-[0.28em] text-[var(--nude)]">Scegli la tua forma</p>
      <div className="my-8 flex justify-center">
        <svg viewBox="-4 -4 108 164" className="h-48 w-32">
          <motion.path d={SHAPES[shape]} animate={{ d: SHAPES[shape] }} transition={{ duration: 0.7, ease: EASE }} fill="url(#nailGrad)" stroke="#C9A45C" strokeWidth="1.2" />
          <defs><linearGradient id="nailGrad" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#EED9D0" /><stop offset="1" stopColor="#C99A8A" /></linearGradient></defs>
        </svg>
      </div>
      <div className="flex flex-wrap gap-2">
        {Object.keys(SHAPES).map((k) => (
          <button key={k} data-testid={`nails-shape-${k.toLowerCase()}`} onMouseEnter={() => setShape(k)} onClick={() => setShape(k)} className={`rounded-full border px-4 py-2 text-xs tracking-wide transition-colors duration-300 ${shape === k ? "border-[var(--gold)] bg-[var(--gold)] text-[var(--ink)]" : "border-white/25 text-[var(--cream)] hover:border-[var(--gold)]"}`}>{k}</button>
        ))}
      </div>
    </Reveal>
  );
};

export const Nails = () => (
  <section id="unghie" data-testid="nails-explainer-section" className="relative bg-[#F1E4DC] py-24 md:py-36">
    <div className="mx-auto max-w-[1400px] px-5 md:px-10">
      <div className="grid items-center gap-14 lg:grid-cols-12 lg:gap-20">
        <ClipImage src="/img/nails-aura.jpeg" alt="Ricostruzione unghie realizzata da AURA" testid="nails-image" className="order-2 aspect-[4/5] w-full lg:order-1 lg:col-span-5" frame="rounded-[2rem] rounded-tl-[160px]" />
        <div className="order-1 lg:order-2 lg:col-span-7">
          <SectionHead num="02" eyebrow="Mani" title={<>Cos'è la <em className="gold-text italic">ricostruzione</em> unghie</>} />
          <Reveal delay={0.1} className="mt-10 space-y-6 text-base leading-relaxed text-[var(--ink-soft)] md:text-lg">
            <p>La ricostruzione unghie è una tecnica estetica che permette di allungare, rinforzare e modellare l'unghia naturale con materiali specifici come gel o acrygel.</p>
            <p>Il materiale viene applicato e modellato strato per strato, poi limato e rifinito fino alla forma scelta. Il risultato sono unghie resistenti, uniformi e curate, la base ideale per colore e nail art.</p>
          </Reveal>
        </div>
      </div>

      <div className="mt-20 grid gap-6 lg:grid-cols-12">
        <div className="grid gap-6 sm:grid-cols-2 lg:col-span-8">
          {TECH.map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.08} data-testid={`nails-tech-${i + 1}`} className={`${i === TECH.length - 1 && TECH.length % 2 ? "sm:col-span-2" : ""}`} >
              <div className="group relative h-full overflow-hidden rounded-[2rem] border border-[var(--gold)]/20 bg-[var(--cream)] p-8 transition-shadow duration-500 hover:shadow-[0_24px_60px_rgba(42,30,27,.08)]">
              <span className="absolute right-6 top-6 font-display text-sm italic text-[var(--gold-deep)]">0{i + 1}</span>
              <h3 className="font-display text-3xl">{t}</h3>
              <p className="mt-4 text-sm leading-relaxed text-[var(--ink-soft)] md:text-base">{d}</p>
              <span className="absolute bottom-0 left-0 h-[2px] w-full origin-left scale-x-0 bg-[var(--gold)] transition-transform duration-700 group-hover:scale-x-100" />
              </div>
            </Reveal>
          ))}
        </div>
        <div className="lg:col-span-4"><ShapePicker /></div>
      </div>
    </div>
  </section>
);
